import React, { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { adminApi, getErrorMessage } from "@/lib/api";
import { LINE_TYPES, SYSTEMS, btnPrimary, selectCls } from "./ui";

const NUMS = [["qty", "Qty"], ["labour_hours", "Labour hrs"], ["labour_rate", "Labour rate"], ["labour_cost", "Labour $"],
  ["parts_cost", "Parts $"], ["materials_cost", "Materials $"], ["line_total", "Line total $ (as printed)"]];
const inputCls = `${selectCls} w-full`;

function Field({ label, children }) {
  return <label className="block text-[0.7rem] text-neutral-500"><span className="block mb-1">{label}</span>{children}</label>;
}

export function LineEditor({ line, onClose, onSaved }) {
  const [f, setF] = useState(() => ({ ...line, benchmark_override: line.benchmark_override || "auto" }));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async () => {
    setBusy(true);
    const toNum = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
    const body = {
      standardised_description: f.standardised_description || "", line_type: f.line_type, system: f.system, component: f.component || "",
      action: f.action, spec: f.spec || null, benchmark_override: f.benchmark_override, reviewed: !!f.reviewed,
      ...Object.fromEntries(NUMS.map(([k]) => [k, toNum(f[k])])),
    };
    try { onSaved((await adminApi.patch(`/admin/wi/lines/${line.id}`, body)).data); toast.success("Line updated — benchmarks recalculated"); onClose(); }
    catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0A0A0A] border-neutral-800 text-neutral-200 max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="wi-line-editor">
        <DialogHeader>
          <DialogTitle className="font-heading text-white">Correct repair line</DialogTitle>
          <DialogDescription className="text-neutral-500">“{line.description}”</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><Field label="Standardised description"><input value={f.standardised_description || ""} onChange={set("standardised_description")} className={inputCls} data-testid="wi-line-std-desc" /></Field></div>
          <Field label="System"><select value={f.system} onChange={set("system")} className={inputCls} data-testid="wi-line-system">{SYSTEMS.map((s) => <option key={s}>{s}</option>)}</select></Field>
          <Field label="Component (standardised)"><input value={f.component || ""} onChange={set("component")} className={inputCls} data-testid="wi-line-component" /></Field>
          <Field label="Line type"><select value={f.line_type} onChange={set("line_type")} className={inputCls} data-testid="wi-line-type">{Object.entries(LINE_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
          <Field label="Action"><select value={f.action} onChange={set("action")} className={inputCls} data-testid="wi-line-action">{["replace", "repair", "diagnose", "other"].map((a) => <option key={a}>{a}</option>)}</select></Field>
          <div className="sm:col-span-2"><Field label="Specification"><input value={f.spec || ""} onChange={set("spec")} className={inputCls} /></Field></div>
          {NUMS.map(([k, label]) => (
            <Field key={k} label={label}><input type="number" step="0.01" min="0" value={f[k] ?? ""} onChange={set(k)} className={inputCls} data-testid={`wi-line-${k}`} /></Field>
          ))}
          <Field label="Use in pricing benchmark">
            <select value={f.benchmark_override} onChange={set("benchmark_override")} className={inputCls} data-testid="wi-line-benchmark">
              <option value="auto">Automatic rules</option><option value="include">Always include</option><option value="exclude">Exclude (not comparable)</option>
            </select>
          </Field>
        </div>
        <label className="flex items-center gap-2 text-xs text-neutral-300 mt-2">
          <input type="checkbox" checked={!!f.reviewed} onChange={set("reviewed")} className="accent-[#73bf44]" data-testid="wi-line-reviewed" /> Mark as reviewed (trusted even if AI confidence was low)
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="text-sm text-neutral-400 hover:text-white px-4 py-2">Cancel</button>
          <button onClick={save} disabled={busy} className={btnPrimary} data-testid="wi-line-save-btn">{busy ? "Saving…" : "Save correction"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
